import { supabase } from '@/lib/supabase';

// ─────────────────────────────────────────────────────────────
// Blog Zifek — lecture publique, compteurs de visites et CRUD admin.
// Les articles publiés alimentent /blog ; les compteurs sont stockés
// dans `blog_visits` (une ligne par ouverture de page ou d'article).
// ─────────────────────────────────────────────────────────────

export interface BlogArticle {
  id: number;
  title: string;
  slug: string | null;
  excerpt: string | null;
  content: string | null;
  cover_image: string | null;
  tags: string | null;
  status: string | null;
  published_at: string | null;
  created_at: string | null;
  updated_at: string | null;
  category_id: number | null;
  /** Commerce propriétaire de l'article (blog du marchand). */
  idcommerce?: number | null;
  author_id?: number | null;
}

export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 90);
}

export async function listPublishedArticles(limit = 60): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('id, title, slug, excerpt, content, cover_image, tags, status, published_at, created_at, updated_at, category_id, idcommerce, author_id')
    .eq('status', 'published')
    .order('published_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

/** Articles publiés d'un commerce (blog de sa boutique). */
export async function listPublishedArticlesForCommerce(commerceId: number, limit = 60): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('id, title, slug, excerpt, content, cover_image, tags, status, published_at, created_at, updated_at, category_id, idcommerce, author_id')
    .eq('status', 'published')
    .eq('idcommerce', commerceId)
    .order('published_at', { ascending: false })
    .limit(limit);
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

/** Articles d'un commerce, brouillons compris (espace marchand). */
export async function listArticlesForCommerce(commerceId: number): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .eq('idcommerce', commerceId)
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

export async function listAllArticles(): Promise<BlogArticle[]> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .order('created_at', { ascending: false });
  if (error) throw error;
  return (data || []) as BlogArticle[];
}

export async function getArticleBySlug(slug: string): Promise<BlogArticle | null> {
  const { data, error } = await supabase
    .from('blog_articles')
    .select('*')
    .eq('slug', slug)
    .maybeSingle();
  if (error) throw error;
  if (data) return data as BlogArticle;
  // Les liens peuvent utiliser l'identifiant quand le slug est absent.
  const numeric = Number(slug);
  if (Number.isFinite(numeric) && numeric > 0) {
    const { data: byId, error: idError } = await supabase
      .from('blog_articles')
      .select('*')
      .eq('id', numeric)
      .maybeSingle();
    if (idError) throw idError;
    return (byId as BlogArticle) || null;
  }
  return null;
}

/** Enregistre une consultation (article précis ou page liste). */
export async function recordBlogVisit(articleId: number | null, path: string): Promise<void> {
  try {
    await supabase.from('blog_visits').insert({ article_id: articleId, path });
  } catch {
    /* les compteurs ne doivent jamais bloquer la lecture */
  }
}

export interface VisitCounts {
  total: number;
  byArticle: Record<number, number>;
}

export async function getVisitCounts(): Promise<VisitCounts> {
  const byArticle: Record<number, number> = {};
  let total = 0;
  try {
    const { data } = await supabase.from('blog_visits').select('article_id');
    (data || []).forEach((row: { article_id: number | null }) => {
      total += 1;
      if (row.article_id != null) {
        byArticle[row.article_id] = (byArticle[row.article_id] || 0) + 1;
      }
    });
  } catch {
    return { total: 0, byArticle };
  }
  return { total, byArticle };
}

export function tagList(tags: string | null): string[] {
  return String(tags || '')
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

/** Rendu simple : titres, listes, gras et paragraphes. */
export function articleParagraphs(content: string | null): { type: 'h2' | 'h3' | 'p' | 'li' | 'quote'; text: string }[] {
  return String(content || '')
    .split(/\n+/)
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      if (line.startsWith('### ')) return { type: 'h3' as const, text: line.slice(4) };
      if (line.startsWith('## ')) return { type: 'h2' as const, text: line.slice(3) };
      if (line.startsWith('# ')) return { type: 'h2' as const, text: line.slice(2) };
      if (line.startsWith('> ')) return { type: 'quote' as const, text: line.slice(2) };
      if (line.startsWith('- ') || line.startsWith('* ')) return { type: 'li' as const, text: line.slice(2) };
      return { type: 'p' as const, text: line };
    });
}

export function readingMinutes(content: string | null): number {
  const words = String(content || '').trim().split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export function formatArticleDate(value: string | null): string {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
}